<template>
  <div class="personal-coding">
    <Navigation />
    <Hero />
    
    <!-- 1. Problem - Welche Probleme ich für dich löse -->
    <section id="problems" class="problems-section">
      <div class="problems-content">
        <h2><strong class="highlight-gradient">{{ $t('problems.title') }}</strong></h2>
        <p class="section-intro">{{ $t('problems.intro') }}</p>
        
        <div class="problems-grid">
          <div class="problem-card">
            <div class="problem-icon">😰</div>
            <h3>{{ $t('problems.problem1Title') }}</h3>
            <p>{{ $t('problems.problem1Text') }}</p>
          </div>
          
          <div class="problem-card">
            <div class="problem-icon">⏰</div>
            <h3>{{ $t('problems.problem2Title') }}</h3>
            <p>{{ $t('problems.problem2Text') }}</p>
          </div>
          
          <div class="problem-card">
            <div class="problem-icon">💸</div>
            <h3>{{ $t('problems.problem3Title') }}</h3>
            <p>{{ $t('problems.problem3Text') }}</p>
          </div>
          
          <div class="problem-card">
            <div class="problem-icon">🔄</div>
            <h3>{{ $t('problems.problem4Title') }}</h3>
            <p>{{ $t('problems.problem4Text') }}</p>
          </div>

          <div class="problem-card">
            <div class="problem-icon">🛠️</div>
            <h3>{{ $t('problems.problem5Title') }}</h3>
            <p>{{ $t('problems.problem5Text') }}</p>
          </div>

          <div class="problem-card">
            <div class="problem-icon">📊</div>
            <h3>{{ $t('problems.problem6Title') }}</h3>
            <p>{{ $t('problems.problem6Text') }}</p>
          </div>
        </div>
        
        <div class="problems-cta">
          <p class="cta-intro">{{ $t('problems.ctaIntro') }}</p>
          <a href="https://app.reclaim.ai/m/dawid-paliczuk/15-min-call" target="_blank" rel="noopener" class="cta-button">
            {{ $t('problems.ctaButton') }}
          </a>
        </div>
      </div>
    </section>
    
    <!-- 2. Lösung - Wie ich diese Probleme löse -->
    <section id="solution" class="demos">
      <div class="solution-statement">
        <span>{{ $t('solution.statement1') }}</span><br>
        <span class="fade-text">{{ $t('solution.statement2') }}</span>
        <span> {{ $t('solution.statement3') }} </span><span class="fade-text">{{ $t('solution.statement4') }}</span><br>
        <span>{{ $t('solution.statement5') }}</span><br>
        <span style="color: var(--text-primary);">{{ $t('solution.statement6') }} </span><span class="fade-text">{{ $t('solution.statement7') }}</span>
      </div>
      <p>{{ $t('solution.intro') }}</p>



      <div class="demo-grid">
        <div id="astrology-demo" class="demo-card">
          <div class="card-icon">🔮</div>
          <h3>{{ $t('solution.astrologyTitle', { chartCalculator: $t('solution.chartCalculator') }) }}</h3>
          <div class="target-audience">
            <p><strong>{{ $t('solution.astrologyQuestion') }}</strong></p>
            <ul>
              <li>{{ $t('hero.tags.astrology') }}</li>
              <li>{{ $t('hero.tags.humanDesign') }}</li>
              <li>{{ $t('hero.tags.numerology') }}</li>
              <li>{{ $t('hero.tags.destinyMatrix') }}</li>
            </ul>
            <p><strong>{{ $t('solution.astrologyAnswer') }}</strong></p>
          </div>
          <div class="usp-cards">
            <div class="usp-card">
              <span class="usp-emoji">⚡</span>
              <div class="usp-content">
                <span class="usp-text">{{ $t('solution.uspCards.astrology.card1Text') }}</span>
                <span class="usp-subtitle">{{ $t('solution.uspCards.astrology.card1Subtitle') }}</span>
              </div>
            </div>
            <div class="usp-card">
              <span class="usp-emoji">🤖</span>
              <div class="usp-content">
                <span class="usp-text">{{ $t('solution.uspCards.astrology.card2Text') }}</span>
                <span class="usp-subtitle">{{ $t('solution.uspCards.astrology.card2Subtitle') }}</span>
              </div>
            </div>
            <div class="usp-card">
              <span class="usp-emoji">📱</span>
              <div class="usp-content">
                <span class="usp-text">{{ $t('solution.uspCards.astrology.card3Text') }}</span>
                <span class="usp-subtitle">{{ $t('solution.uspCards.astrology.card3Subtitle') }}</span>
              </div>
            </div>
            <div class="usp-card">
              <span class="usp-emoji">💬</span>
              <div class="usp-content">
                <span class="usp-text">{{ $t('solution.uspCards.astrology.card4Text') }}</span>
                <span class="usp-subtitle">{{ $t('solution.uspCards.astrology.card4Subtitle') }}</span>
              </div>
            </div>
          </div>
          <AstrologyQRGenerator @instagram-button-click="openInstagramButton" />
        </div>

        <div id="meditation-demo" class="demo-card">
          <div class="card-icon">🧘‍♀️</div>
          <h3>{{ $t('solution.meditationTitle', { habitTracker: $t('solution.habitTracker') }) }}</h3>
          <div class="target-audience">
            <p><strong>{{ $t('solution.meditationQuestion') }}</strong></p>
            <ul>
              <li>{{ $t('hero.tags.meditation') }}</li>
              <li>{{ $t('hero.tags.breathwork') }}</li>
              <li>{{ $t('solution.meditationHypnosis') }}</li>
              <li>{{ $t('hero.tags.yoga') }}</li>
            </ul>
            <p><strong>{{ $t('solution.meditationAnswer') }}</strong></p>
          </div>
          <div class="usp-cards">
            <div class="usp-card">
              <span class="usp-emoji">🤖</span>
              <div class="usp-content">
                <span class="usp-text">{{ $t('solution.uspCards.meditation.card1Text') }}</span>
                <span class="usp-subtitle">{{ $t('solution.uspCards.meditation.card1Subtitle') }}</span>
              </div>
            </div>
            <div class="usp-card">
              <span class="usp-emoji">💬</span>
              <div class="usp-content">
                <span class="usp-text">{{ $t('solution.uspCards.meditation.card2Text') }}</span>
                <span class="usp-subtitle">{{ $t('solution.uspCards.meditation.card2Subtitle') }}</span>
              </div>
            </div>
            <div class="usp-card">
              <span class="usp-emoji">📊</span>
              <div class="usp-content">
                <span class="usp-text">{{ $t('solution.uspCards.meditation.card3Text') }}</span>
                <span class="usp-subtitle">{{ $t('solution.uspCards.meditation.card3Subtitle') }}</span>
              </div>
            </div>
            <div class="usp-card">
              <span class="usp-emoji">🎯</span>
              <div class="usp-content">
                <span class="usp-text">{{ $t('solution.uspCards.meditation.card4Text') }}</span>
                <span class="usp-subtitle">{{ $t('solution.uspCards.meditation.card4Subtitle') }}</span>
              </div>
            </div>
          </div>
          <QRCodeGenerator />
        </div>
      </div>
      
      <div class="demo-cta">
        <p class="demo-cta-text">{{ $t('solution.ctaText') }}</p>
        <a href="https://app.reclaim.ai/m/dawid-paliczuk/15-min-call" target="_blank" rel="noopener" class="cta-button">
          {{ $t('solution.ctaButton') }}
        </a>
      </div>
    </section>

    <!-- 3. Social Proof - Das sagen meine Kunden -->
    <Partners />

    <!-- 4. USP/Mehrwert - Darum bin ich der Richtige für dich -->
    <section id="usp" class="offer-blocks">
      <div class="container">
        <h2><strong>{{ $t('usp.title') }}</strong></h2>
        <p class="section-intro section-intro--center">{{ $t('usp.intro') }}</p>
        
        <div class="offer-grid">
          <!-- Block 1: Spiritualität + Technologie -->
          <div class="offer-block">
            <div class="block-header">
              <div class="block-icon">🧠</div>
              <h3>{{ $t('usp.block1Title') }}</h3>
              <p class="block-subtitle">{{ $t('usp.block1Subtitle') }}</p>
            </div>
            
            <div class="block-benefits">
              <div class="benefit-item">
                <span class="benefit-icon">🎯</span>
                <span>{{ $t('usp.block1Benefit1') }}</span>
              </div>
              <div class="benefit-item">
                <span class="benefit-icon">🤖</span>
                <span>{{ $t('usp.block1Benefit2') }}</span>
              </div>
              <div class="benefit-item">
                <span class="benefit-icon">💎</span>
                <span>{{ $t('usp.block1Benefit3') }}</span>
              </div>
              <div class="benefit-item">
                <span class="benefit-icon">🔄</span>
                <span>{{ $t('usp.block1Benefit4') }}</span>
              </div>
              </div>
            </div>
            
          <!-- Block 2: Persönliche Betreuung -->
          <div class="offer-block">
            <div class="block-header">
              <div class="block-icon">🤝</div>
              <h3>{{ $t('usp.block2Title') }}</h3>
              <p class="block-subtitle">{{ $t('usp.block2Subtitle') }}</p>
            </div>
            
            <div class="block-benefits">
              <div class="benefit-item">
                <span class="benefit-icon">📞</span>
                <span>{{ $t('usp.block2Benefit1') }}</span>
              </div>
              <div class="benefit-item">
                <span class="benefit-icon">🎯</span>
                <span>{{ $t('usp.block2Benefit2') }}</span>
              </div>
              <div class="benefit-item">
                <span class="benefit-icon">⚡</span>
                <span>{{ $t('usp.block2Benefit3') }}</span>
              </div>
              <div class="benefit-item">
                <span class="benefit-icon">🔧</span>
                <span>{{ $t('usp.block2Benefit4') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 3. Hauptangebot -->
    <section id="angebot" class="specialization">
      <div class="specialization-content">
        <h2 class="comparison-main-title"><strong>{{ $t('comparison.title') }}</strong></h2>

        <div class="specialization-benefits">
          <div class="alternatives-comparison">
            <div class="alternatives-grid">
              <div class="alternative-column">
                <div class="alternative-title">{{ $t('comparison.manualTitle') }}</div>
                <div class="alternative-features">
                  <div class="feature-item negative">
                    <span class="feature-icon">❌</span>
                    <span class="feature-text">{{ $t('comparison.manual1') }}</span>
                  </div>
                  <div class="feature-item negative">
                    <span class="feature-icon">❌</span>
                    <span class="feature-text">{{ $t('comparison.manual2') }}</span>
                  </div>
                  <div class="feature-item negative">
                    <span class="feature-icon">❌</span>
                    <span class="feature-text">{{ $t('comparison.manual3') }}</span>
                  </div>
                  <div class="feature-item negative">
                    <span class="feature-icon">❌</span>
                    <span class="feature-text">{{ $t('comparison.manual4') }}</span>
                  </div>
                  <div class="feature-item negative">
                    <span class="feature-icon">❌</span>
                    <span class="feature-text">{{ $t('comparison.manual5') }}</span>
                  </div>
                </div>
              </div>
              
              <div class="alternative-column">
                <div class="alternative-title">{{ $t('comparison.emailTitle') }}</div>
                <div class="alternative-features">
                  <div class="feature-item negative">
                    <span class="feature-icon">❌</span>
                    <span class="feature-text">{{ $t('comparison.email1') }}</span>
                  </div>
                  <div class="feature-item negative">
                    <span class="feature-icon">❌</span>
                    <span class="feature-text">{{ $t('comparison.email2') }}</span>
                  </div>
                  <div class="feature-item negative">
                    <span class="feature-icon">❌</span>
                    <span class="feature-text">{{ $t('comparison.email3') }}</span>
                  </div>
                  <div class="feature-item negative">
                    <span class="feature-icon">❌</span>
                    <span class="feature-text">{{ $t('comparison.email4') }}</span>
                  </div>
                  <div class="feature-item negative">
                    <span class="feature-icon">❌</span>
                    <span class="feature-text">{{ $t('comparison.email5') }}</span>
                  </div>
                </div>
              </div>
              
              <div class="alternative-column">
                <div class="alternative-title">{{ $t('comparison.automationTitle') }}</div>
                <div class="alternative-features">
                  <div class="feature-item positive">
                    <span class="feature-icon">✅</span>
                    <span class="feature-text">{{ $t('comparison.automation1') }}</span>
                  </div>
                  <div class="feature-item positive">
                    <span class="feature-icon">✅</span>
                    <span class="feature-text">{{ $t('comparison.automation2') }}</span>
                  </div>
                  <div class="feature-item positive">
                    <span class="feature-icon">✅</span>
                    <span class="feature-text">{{ $t('comparison.automation3') }}</span>
                  </div>
                  <div class="feature-item positive">
                    <span class="feature-icon">✅</span>
                    <span class="feature-text">{{ $t('comparison.automation4') }}</span>
                  </div>
                  <div class="feature-item positive">
                    <span class="feature-icon">✅</span>
                    <span class="feature-text">{{ $t('comparison.automation5') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <h3 class="benefits-title">{{ $t('comparison.benefitsTitle') }}</h3>
          <ul class="benefits-list">
            <li>
              <div class="benefit-icon">🎯</div>
              <div class="benefit-content">
                <strong>{{ $t('comparison.benefit1Title') }}</strong>
                <span class="benefit-description">{{ $t('comparison.benefit1Desc') }}</span>
              </div>
            </li>
            <li>
              <div class="benefit-icon">🤖</div>
              <div class="benefit-content">
                <strong>{{ $t('comparison.benefit2Title') }}</strong>
                <span class="benefit-description">{{ $t('comparison.benefit2Desc') }}</span>
              </div>
            </li>
            <li>
              <div class="benefit-icon">🧠</div>
              <div class="benefit-content">
                <strong>{{ $t('comparison.benefit3Title') }}</strong>
                <span class="benefit-description">{{ $t('comparison.benefit3Desc') }}</span>
              </div>
            </li>
            <li>
              <div class="benefit-icon">💫</div>
              <div class="benefit-content">
                <strong>{{ $t('comparison.benefit4Title') }}</strong>
                <span class="benefit-description">{{ $t('comparison.benefit4Desc') }}</span>
              </div>
            </li>
            <li>
              <div class="benefit-icon">🌈</div>
              <div class="benefit-content">
                <strong>{{ $t('comparison.benefit5Title') }}</strong>
                <span class="benefit-description">{{ $t('comparison.benefit5Desc') }}</span>
              </div>
            </li>
            <li>
              <div class="benefit-icon">🔄</div>
              <div class="benefit-content">
                <strong>{{ $t('comparison.benefit6Title') }}</strong>
                <span class="benefit-description">{{ $t('comparison.benefit6Desc') }}</span>
              </div>
            </li>
          </ul>
        </div>
        
        <div class="benefits-cta">
          <a href="https://app.reclaim.ai/m/dawid-paliczuk/15-min-call" target="_blank" rel="noopener" class="cta-button">
            {{ $t('comparison.ctaButton') }}
          </a>
        </div>
      </div>
    </section>

    <!-- NERD-Methode entfernt - archiviert in src/components/archive/NerdMethode.vue -->

    <!-- 6. Bonus als zusätzlicher Anreiz -->
    <section class="bonus-section">
      <div class="bonus-content">
        <div class="bonus-header">
          <div class="bonus-tag-container">
            <span class="bonus-tag">{{ $t('bonus.badge') }}</span>
            <span class="bonus-tag bonus-tag-shadow">{{ $t('bonus.badge') }}</span>
            <span class="bonus-tag bonus-tag-shadow">{{ $t('bonus.badge') }}</span>
              </div>
            </div>

        <div class="bonus-card">
          <div class="card-header">
            <div class="card-icon">📨</div>
            <h2>{{ $t('bonus.title') }}</h2>
          </div>
          
          <div class="comparison-section">
            <div class="comparison-header">
              <h4>{{ $t('bonus.comparisonTitle') }}</h4>
              </div>
            <div class="comparison-grid">
              <div class="comparison-column">
                <div class="comparison-title">{{ $t('bonus.emailTitle') }}</div>
                <div class="comparison-stats">
                  <div class="stat-highlight">
                    <span class="stat-number">20-25%</span>
                    <span class="stat-label">{{ $t('bonus.emailOpenRate') }}</span>
            </div>
                  <div class="stat-highlight">
                    <span class="stat-number">2-5%</span>
                    <span class="stat-label">{{ $t('bonus.emailClickRate') }}</span>
          </div>
                  <div class="stat-highlight">
                    <span class="stat-number">Spam</span>
                    <span class="stat-label">{{ $t('bonus.emailSpam') }}</span>
              </div>
            </div>
                <ul class="comparison-benefits">
                  <li>❌ {{ $t('bonus.email1') }}</li>
                  <li>❌ {{ $t('bonus.email2') }}</li>
                  <li>❌ {{ $t('bonus.email3') }}</li>
                  <li>❌ {{ $t('bonus.email4') }}</li>
                </ul>
          </div>
              
              <div class="comparison-column">
                <div class="comparison-title">{{ $t('bonus.chatTitle') }}</div>
                <div class="comparison-stats">
                  <div class="stat-highlight">
                    <span class="stat-number">80%+</span>
                    <span class="stat-label">{{ $t('bonus.chatReadRate') }}</span>
              </div>
                  <div class="stat-highlight">
                    <span class="stat-number">40%+</span>
                    <span class="stat-label">{{ $t('bonus.chatClickRate') }}</span>
            </div>
                  <div class="stat-highlight">
                    <span class="stat-number">24/7</span>
                    <span class="stat-label">{{ $t('bonus.chatAvailability') }}</span>
          </div>
        </div>
                <ul class="comparison-benefits">
                  <li>✅ {{ $t('bonus.chat1') }}</li>
                  <li>✅ {{ $t('bonus.chat2') }}</li>
                  <li>✅ {{ $t('bonus.chat3') }}</li>
                  <li>✅ {{ $t('bonus.chat4') }}</li>
                </ul>
      </div>
            </div>
          </div>
          <div class="newsletter-features">
            <div class="instagram-chat-demo">
              <p class="ai-demo-label" role="note">{{ $t('aiDisclosure.demoLabel') }}</p>
              <div class="chat-header">
                  <div class="chat-profile">
                  <div class="profile-pic">📱</div>
                  <div class="chat-info">
                    <div class="chat-name">{{ $t('bonus.chatName') }}</div>
                    <div class="chat-status">{{ $t('bonus.chatStatus') }}</div>
                  </div>
                </div>
                  <div class="chat-scroll-cta"><strong>{{ $t('bonus.chatScrollCta') }}</strong></div>
              </div>
              
              <div class="chat-messages">
                <div class="message outgoing">
                  <div class="message-bubble">
                    {{ $t('bonus.chatMessages.msg1') }}
                  </div>
                  <div class="message-time">10:15</div>
                </div>
                
                <div class="message incoming">
                  <div class="message-bubble">
                    {{ $t('bonus.chatMessages.msg2') }}
                  </div>
                  <div class="message-time">10:16</div>
            </div>
            
                <div class="message outgoing">
                  <div class="message-bubble">
                    {{ $t('bonus.chatMessages.msg3') }}
                  </div>
                  <div class="message-time">10:17</div>
            </div>
            
                <div class="message incoming">
                  <div class="message-bubble">
                    {{ $t('bonus.chatMessages.msg4') }}
                  </div>
                  <div class="message-time">10:18</div>
            </div>
            
                <div class="message incoming">
                  <div class="message-bubble">
                    {{ $t('bonus.chatMessages.msg5') }}
            </div>
                  <div class="message-time">10:18</div>
          </div>
          
                <div class="message outgoing">
                  <div class="message-bubble">
                    {{ $t('bonus.chatMessages.msg6') }}
                  </div>
                  <div class="message-time">10:19</div>
        </div>
        
                <div class="message incoming">
                  <div class="message-bubble">
                    {{ $t('bonus.chatMessages.msg7') }}
                  </div>
                  <div class="message-time">10:20</div>
                </div>
                
                <div class="message incoming">
                  <div class="message-bubble">
                    {{ $t('bonus.chatMessages.msg8') }}
                  </div>
                  <div class="message-time">10:21</div>
                </div>
                
                <div class="message incoming">
                  <div class="message-bubble">
                    {{ $t('bonus.chatMessages.msg9') }}
                  </div>
                  <div class="message-time">10:21</div>
                </div>
                
                <div class="message incoming">
                  <div class="message-bubble">
                    {{ $t('bonus.chatMessages.msg10') }}
                  </div>
                  <div class="message-time">10:22</div>
                </div>
                
                <div class="message outgoing">
                  <div class="message-bubble">
                    {{ $t('bonus.chatMessages.msg11') }}
                  </div>
                  <div class="message-time">10:23</div>
                </div>
                
                <div class="message incoming highlight-message">
                  <div class="message-bubble">
                    <strong>{{ $t('bonus.chatMessages.msg12') }}</strong>
                  </div>
                  <div class="message-time">10:24</div>
                </div>
              </div>
              
              <div class="chat-cta">
                <a href="https://app.reclaim.ai/m/dawid-paliczuk/15-min-call" target="_blank" rel="noopener" class="chat-cta-button">
                  {{ $t('bonus.chatCtaButton') }}
                </a>
              </div>
            </div>
            
            <!-- Quiz Überschrift außerhalb des Badges -->
            <h3 class="quiz-main-title"><strong>{{ $t('bonus.quizTitle') }}</strong></h3>
            
            <div class="interactive-quiz">
              <div class="quiz-container">
                <div class="quiz-question" v-if="!selectedBranch">
                  <p id="quiz-branch-selection" class="quiz-instruction">{{ $t('bonus.quizInstruction') }}</p>
                  <div class="branch-badges">
                    <button @click="selectBranch('astrologie')" class="branch-badge">
                      {{ $t('bonus.branches.astrology') }}
                    </button>
                    <button @click="selectBranch('humandesign')" class="branch-badge">
                      ⚡ Human Design
                    </button>
                    <button @click="selectBranch('numerologie')" class="branch-badge">
                      {{ $t('bonus.branches.numerology') }}
                    </button>
                    <button @click="selectBranch('destinymatrix')" class="branch-badge">
                      🌟 Destiny Matrix
                    </button>
                    <button @click="selectBranch('meditation')" class="branch-badge">
                      🧘‍♀️ Meditation
                    </button>
                    <button @click="selectBranch('breathwork')" class="branch-badge">
                      💨 Breathwork
                    </button>
                    <button @click="selectBranch('yoga')" class="branch-badge">
                      {{ $t('bonus.branches.yoga') }}
                    </button>
                    <button @click="selectBranch('coaching')" class="branch-badge">
                      💼 Coaching
                    </button>
                  </div>
                </div>

                <div class="quiz-result" v-if="selectedBranch">
                  <!-- Astrologie -->
                  <div class="branch-example" v-if="selectedBranch === 'astrologie'">
                    <h6>{{ $t('bonus.astrologyQuiz.title') }}</h6>
                    <div class="example-scenario">
                      
                      <div class="process-step start-step">
                        <div class="step-number">Start</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.astrologyQuiz.event') }}</strong> {{ $t('bonus.astrologyQuiz.eventText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">1.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.astrologyQuiz.send') }}</strong> {{ $t('bonus.astrologyQuiz.sendText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">2.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.astrologyQuiz.build') }}</strong> {{ $t('bonus.astrologyQuiz.buildText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">3.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.astrologyQuiz.out') }}</strong> {{ $t('bonus.astrologyQuiz.outText') }}</p>
                        </div>
                      </div>
                      
                      <div class="sales-flow">
                        <p><strong>{{ $t('bonus.astrologyQuiz.offers') }}</strong></p>
                        <ul>
                          <li>{{ $t('bonus.astrologyQuiz.offer1') }}</li>
                          <li>{{ $t('bonus.astrologyQuiz.offer2') }}</li>
                          <li>{{ $t('bonus.astrologyQuiz.offer3') }}</li>
                          <li>{{ $t('bonus.astrologyQuiz.offer4') }}</li>
                          <li>{{ $t('bonus.astrologyQuiz.offer5') }}</li>
                          <li>{{ $t('bonus.astrologyQuiz.offer6') }}</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <!-- Human Design -->
                  <div class="branch-example" v-if="selectedBranch === 'humandesign'">
                    <h6>{{ $t('bonus.humanDesignQuiz.title') }}</h6>
                    <div class="example-scenario">
                      
                      <div class="process-step start-step">
                        <div class="step-number">Start</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.humanDesignQuiz.event') }}</strong> {{ $t('bonus.humanDesignQuiz.eventText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">1.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.humanDesignQuiz.send') }}</strong> {{ $t('bonus.humanDesignQuiz.sendText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">2.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.humanDesignQuiz.build') }}</strong> {{ $t('bonus.humanDesignQuiz.buildText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">3.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.humanDesignQuiz.out') }}</strong> {{ $t('bonus.humanDesignQuiz.outText') }}</p>
                        </div>
                      </div>
                      
                      <div class="sales-flow">
                        <p><strong>{{ $t('bonus.humanDesignQuiz.offers') }}</strong></p>
                        <ul>
                          <li>{{ $t('bonus.humanDesignQuiz.offer1') }}</li>
                          <li>{{ $t('bonus.humanDesignQuiz.offer2') }}</li>
                          <li>{{ $t('bonus.humanDesignQuiz.offer3') }}</li>
                          <li>{{ $t('bonus.humanDesignQuiz.offer4') }}</li>
                          <li>{{ $t('bonus.humanDesignQuiz.offer5') }}</li>
                          <li>{{ $t('bonus.humanDesignQuiz.offer6') }}</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <!-- Numerologie -->
                  <div class="branch-example" v-if="selectedBranch === 'numerologie'">
                    <h6>{{ $t('bonus.numerologyQuiz.title') }}</h6>
                    <div class="example-scenario">
                      <div class="process-step start-step">
                        <div class="step-number">Start</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.numerologyQuiz.event') }}</strong> {{ $t('bonus.numerologyQuiz.eventText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">1.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.numerologyQuiz.send') }}</strong> {{ $t('bonus.numerologyQuiz.sendText') }}</p>
                          <p class="note">{{ $t('bonus.numerologyQuiz.note') }} <strong>{{ $t('bonus.numerologyQuiz.noteText') }}</strong></p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">2.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.numerologyQuiz.build') }}</strong> {{ $t('bonus.numerologyQuiz.buildText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">3.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.numerologyQuiz.out') }}</strong> {{ $t('bonus.numerologyQuiz.outText') }}</p>
                        </div>
                      </div>

                      <div class="sales-flow">
                        <p><strong>{{ $t('bonus.numerologyQuiz.offers') }}</strong></p>
                        <ul>
                          <li>{{ $t('bonus.numerologyQuiz.offer1') }}</li>
                          <li>{{ $t('bonus.numerologyQuiz.offer2') }}</li>
                          <li>{{ $t('bonus.numerologyQuiz.offer3') }}</li>
                          <li>{{ $t('bonus.numerologyQuiz.offer4') }}</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <!-- Destiny Matrix -->
                  <div class="branch-example" v-if="selectedBranch === 'destinymatrix'">
                    <h6>{{ $t('bonus.destinyMatrixQuiz.title') }}</h6>
                    <div class="example-scenario">
                      <div class="process-step start-step">
                        <div class="step-number">Start</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.destinyMatrixQuiz.event') }}</strong> {{ $t('bonus.destinyMatrixQuiz.eventText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">1.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.destinyMatrixQuiz.send') }}</strong> {{ $t('bonus.destinyMatrixQuiz.sendText') }}</p>
                          <p class="note">{{ $t('bonus.destinyMatrixQuiz.note') }} <strong>{{ $t('bonus.destinyMatrixQuiz.noteText') }}</strong></p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">2.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.destinyMatrixQuiz.build') }}</strong> {{ $t('bonus.destinyMatrixQuiz.buildText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">3.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.destinyMatrixQuiz.out') }}</strong> {{ $t('bonus.destinyMatrixQuiz.outText') }}</p>
                        </div>
                      </div>

                      <div class="sales-flow">
                        <p><strong>{{ $t('bonus.destinyMatrixQuiz.offers') }}</strong></p>
                        <ul>
                          <li>{{ $t('bonus.destinyMatrixQuiz.offer1') }}</li>
                          <li>{{ $t('bonus.destinyMatrixQuiz.offer2') }}</li>
                          <li>{{ $t('bonus.destinyMatrixQuiz.offer3') }}</li>
                          <li>{{ $t('bonus.destinyMatrixQuiz.offer4') }}</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <!-- Meditation -->
                  <div class="branch-example" v-if="selectedBranch === 'meditation'">
                    <h6>{{ $t('bonus.meditationQuiz.title') }}</h6>
                    <div class="example-scenario">
                      
                      <div class="process-step start-step">
                        <div class="step-number">Start</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.meditationQuiz.event') }}</strong> {{ $t('bonus.meditationQuiz.eventText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">1.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.meditationQuiz.send') }}</strong> {{ $t('bonus.meditationQuiz.sendText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">2.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.meditationQuiz.motivate') }}</strong> {{ $t('bonus.meditationQuiz.motivateText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">3.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.meditationQuiz.sendMsg') }}</strong> {{ $t('bonus.meditationQuiz.sendMsgText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">4.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.meditationQuiz.startAgain') }}</strong> {{ $t('bonus.meditationQuiz.startAgainText') }}</p>
                        </div>
                      </div>
                      
                      <div class="sales-flow">
                        <p><strong>{{ $t('bonus.meditationQuiz.offers') }}</strong></p>
                        <ul>
                          <li>{{ $t('bonus.meditationQuiz.offer1') }}</li>
                          <li>{{ $t('bonus.meditationQuiz.offer2') }}</li>
                          <li>{{ $t('bonus.meditationQuiz.offer3') }}</li>
                          <li>{{ $t('bonus.meditationQuiz.offer4') }}</li>
                          <li>{{ $t('bonus.meditationQuiz.offer5') }}</li>
                          <li>{{ $t('bonus.meditationQuiz.offer6') }}</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <!-- Breathwork -->
                  <div class="branch-example" v-if="selectedBranch === 'breathwork'">
                    <h6>{{ $t('bonus.breathworkQuiz.title') }}</h6>
                    <div class="example-scenario">
                      
                      <div class="process-step start-step">
                        <div class="step-number">Start</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.breathworkQuiz.event') }}</strong> {{ $t('bonus.breathworkQuiz.eventText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">1.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.breathworkQuiz.send') }}</strong> {{ $t('bonus.breathworkQuiz.sendText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">2.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.breathworkQuiz.motivate') }}</strong> {{ $t('bonus.breathworkQuiz.motivateText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">3.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.breathworkQuiz.sendMsg') }}</strong> {{ $t('bonus.breathworkQuiz.sendMsgText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">4.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.breathworkQuiz.startAgain') }}</strong> {{ $t('bonus.breathworkQuiz.startAgainText') }}</p>
                        </div>
                      </div>
                      
                      <div class="sales-flow">
                        <p><strong>{{ $t('bonus.breathworkQuiz.offers') }}</strong></p>
                        <ul>
                          <li>{{ $t('bonus.breathworkQuiz.offer1') }}</li>
                          <li>{{ $t('bonus.breathworkQuiz.offer2') }}</li>
                          <li>{{ $t('bonus.breathworkQuiz.offer3') }}</li>
                          <li>{{ $t('bonus.breathworkQuiz.offer4') }}</li>
                          <li>{{ $t('bonus.breathworkQuiz.offer5') }}</li>
                          <li>{{ $t('bonus.breathworkQuiz.offer6') }}</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <!-- Yoga -->
                  <div class="branch-example" v-if="selectedBranch === 'yoga'">
                    <h6>{{ $t('bonus.yogaQuiz.title') }}</h6>
                    <div class="example-scenario">
                      
                      <div class="process-step start-step">
                        <div class="step-number">Start</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.yogaQuiz.event') }}</strong> {{ $t('bonus.yogaQuiz.eventText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">1.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.yogaQuiz.send') }}</strong> {{ $t('bonus.yogaQuiz.sendText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">2.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.yogaQuiz.motivate') }}</strong> {{ $t('bonus.yogaQuiz.motivateText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">3.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.yogaQuiz.sendMsg') }}</strong> {{ $t('bonus.yogaQuiz.sendMsgText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">4.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.yogaQuiz.startAgain') }}</strong> {{ $t('bonus.yogaQuiz.startAgainText') }}</p>
                        </div>
                      </div>
                      
                      <div class="sales-flow">
                        <p><strong>{{ $t('bonus.yogaQuiz.offers') }}</strong></p>
                        <ul>
                          <li>{{ $t('bonus.yogaQuiz.offer1') }}</li>
                          <li>{{ $t('bonus.yogaQuiz.offer2') }}</li>
                          <li>{{ $t('bonus.yogaQuiz.offer3') }}</li>
                          <li>{{ $t('bonus.yogaQuiz.offer4') }}</li>
                          <li>{{ $t('bonus.yogaQuiz.offer5') }}</li>
                          <li>{{ $t('bonus.yogaQuiz.offer6') }}</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <!-- Coaching -->
                  <div class="branch-example" v-if="selectedBranch === 'coaching'">
                    <h6>{{ $t('bonus.coachingQuiz.title') }}</h6>
                    <div class="example-scenario">
                      
                      <div class="process-step start-step">
                        <div class="step-number">Start</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.coachingQuiz.event') }}</strong> {{ $t('bonus.coachingQuiz.eventText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">1.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.coachingQuiz.send') }}</strong> {{ $t('bonus.coachingQuiz.sendText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">2.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.coachingQuiz.motivate') }}</strong> {{ $t('bonus.coachingQuiz.motivateText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">3.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.coachingQuiz.sendMsg') }}</strong> {{ $t('bonus.coachingQuiz.sendMsgText') }}</p>
                        </div>
                      </div>

                      <div class="process-step">
                        <div class="step-number">4.</div>
                        <div class="step-content">
                          <p><strong>{{ $t('bonus.coachingQuiz.startAgain') }}</strong> {{ $t('bonus.coachingQuiz.startAgainText') }}</p>
                        </div>
                      </div>
                      
                      <div class="sales-flow">
                        <p><strong>{{ $t('bonus.coachingQuiz.offers') }}</strong></p>
                        <ul>
                          <li>{{ $t('bonus.coachingQuiz.offer1') }}</li>
                          <li>{{ $t('bonus.coachingQuiz.offer2') }}</li>
                          <li>{{ $t('bonus.coachingQuiz.offer3') }}</li>
                          <li>{{ $t('bonus.coachingQuiz.offer4') }}</li>
                          <li>{{ $t('bonus.coachingQuiz.offer5') }}</li>
                          <li>{{ $t('bonus.coachingQuiz.offer6') }}</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div class="reset-row">
                    <button @click="resetQuiz" class="reset-btn">{{ $t('bonus.quizReset') }}</button>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>

    <!-- 7. Story für Vertrauensaufbau -->
    <section id="story" class="about">
      <div class="content">
        <h2 class="story-title"><strong>{{ $t('story.title') }}</strong></h2>
        <div class="timeline">
          <div class="timeline-item">
            <div class="timeline-content">
              <div class="timeline-row">
              <span class="timeline-date">1998</span>
                <div class="timeline-emoji">🇵🇱 👶 ✨</div>
              </div>
              <p>{{ $t('story.year1998') }}</p>
            </div>
          </div>

          <div class="timeline-item">
            <div class="timeline-content">
              <div class="timeline-row">
              <span class="timeline-date">2003</span>
                <div class="timeline-emoji">🚗 🇩🇪 🌟</div>
              </div>
              <p>{{ $t('story.year2003') }}</p>
            </div>
          </div>

          <div class="timeline-item">
            <div class="timeline-content">
              <div class="timeline-row">
              <span class="timeline-date">2012</span>
                <div class="timeline-emoji">💻 🎮 🤓</div>
              </div>
              <p>{{ $t('story.year2012') }}</p>
            </div>
          </div>

          <div class="timeline-item">
            <div class="timeline-content">
              <div class="timeline-row">
              <span class="timeline-date">2018</span>
                <div class="timeline-emoji">🔄 💔 📚</div>
              </div>
              <p>{{ $t('story.year2018') }}</p>
            </div>
          </div>

          <div class="timeline-item">
            <div class="timeline-content">
              <div class="timeline-row">
              <span class="timeline-date">2023</span>
                <div class="timeline-emoji">🎓 💻 🌱</div>
              </div>
              <p>{{ $t('story.year2023') }}</p>
            </div>
          </div>

          <div class="timeline-item">
            <div class="timeline-content">
              <div class="timeline-row">
              <span class="timeline-date">{{ $t('story.todayLabel') }}</span>
                <div class="timeline-emoji">🚀 🔮 ✨</div>
              </div>
              <p>{{ $t('story.yearToday') }}</p>
            </div>
          </div>
        </div>
        
        <div class="story-cta">
          <a href="https://app.reclaim.ai/m/dawid-paliczuk/15-min-call" target="_blank" rel="noopener" class="cta-button">
            {{ $t('story.ctaButton') }}
          </a>
        </div>
      </div>
    </section>

    <!-- 8. Abschließender Call-to-Action -->
    <section class="cta">
      <h3><strong>{{ $t('finalCta.title') }}</strong></h3>
      <div class="final-cta">
        <p class="final-cta-text">{{ $t('finalCta.text') }}</p>
        <a href="https://app.reclaim.ai/m/dawid-paliczuk/15-min-call" target="_blank" rel="noopener" class="cta-button primary-cta">
          {{ $t('finalCta.button') }}
        </a>
        <p class="cta-guarantee">{{ $t('finalCta.guarantee') }}</p>
          </div>
      <p class="cta-subtext">{{ $t('finalCta.subtext') }}</p>
    </section>

    <!-- Newsletter CTA -->
    <section id="newsletter" class="newsletter-section">
      <div class="newsletter-content">
        <h3 class="newsletter-title">{{ $t('newsletter.title') }}</h3>
        
        <!-- SendPulse Subscription Form -->
        <div class="newsletter-form-wrapper">
          <div class="sp-form-outer">
            <div id="sp-form-249488" sp-id="249488" sp-hash="392bb737e04e2ed4e2fe42c49200442cacdea57e1c67a30481aa768948253028" sp-lang="de" class="sp-form sp-form-regular sp-form-embed sp-form-full-width" sp-show-options="%7B%22satellite%22%3Afalse%2C%22maDomain%22%3A%22login.sendpulse.com%22%2C%22formsDomain%22%3A%22forms.sendpulse.com%22%2C%22condition%22%3A%22onButtonClick%22%2C%22scrollTo%22%3A100%2C%22delay%22%3A0%2C%22repeat%22%3A0%2C%22background%22%3A%22rgba(0%2C%200%2C%200%2C%200.5)%22%2C%22position%22%3A%22bottom-right%22%2C%22animation%22%3A%22%22%2C%22hideOnMobile%22%3Afalse%2C%22submitRedirectUrl%22%3A%22%22%2C%22urlFilter%22%3Afalse%2C%22urlFilterConditions%22%3A%5B%7B%22force%22%3A%22hide%22%2C%22clause%22%3A%22ends%22%2C%22token%22%3A%22%22%7D%5D%2C%22analytics%22%3A%7B%22ga%22%3A%7B%22eventLabel%22%3A%22Subscription_form_Dawid_ChatBots_f_r_Astrologie_Human_Design_Numerologie%22%2C%22send%22%3Afalse%7D%7D%2C%22utmEnable%22%3Afalse%7D">
              <div class="sp-form-fields-wrapper show-grid">
                <div class="sp-message"><div></div></div>
                <form novalidate="" class="sp-element-container sp-lg sp-field-nolabel ">
                  <div class="sp-field " sp-id="sp-8e0d365e-ef9e-49a2-a6af-086dc7a8f2f2">
                    <div style="font-family: inherit; line-height: 1.5;">
                      <p><strong><span style="font-size: 24px;">{{ $t('newsletter.header') }}</span></strong></p>
                      <p>{{ $t('newsletter.description') }}</p>
                      <p>{{ $t('newsletter.benefit1') }}<br>{{ $t('newsletter.benefit2') }}<br>{{ $t('newsletter.benefit3') }}</p>
            </div>
                  </div>
                  <div class="sp-field " sp-id="sp-5fef9934-7e95-4e28-bbc6-a927da8a67b0">
                    <div class="sp-checkbox-option">
                      <input
                        type="checkbox"
                        id="newsletter-consent"
                        sp-type="gdprConfirm"
                        name="sform[Z2RwckNvbmZpcm0=]"
                        value="yes"
                        sp-tips="%7B%22required%22%3A%22Pflichtfeld%22%7D"
                        class=" "
                        required="required"
                      >
                      <label for="newsletter-consent">
                        <span v-html="newsletterConsentHtml"></span>&nbsp;<span><strong>*</strong></span>
                      </label>
                    </div>
                  </div>
                  <div class="sp-field " sp-id="sp-69235624-6130-40d3-946f-a2c15876e167">
                    <label class="sp-control-label"><span >{{ $t('newsletter.nameLabel') }}</span><strong >*</strong></label>
                    <input type="text" sp-type="input" name="sform[TmFtZQ==]" class="sp-form-control " :placeholder="$t('newsletter.namePlaceholder')" sp-tips="%7B%22required%22%3A%22Pflichtfeld%22%7D" autocomplete="on" required="required">
                </div>
                  <div class="sp-field " sp-id="sp-f1e5e65a-3c4a-4ca1-b0b5-ce4fc0efa21d">
                    <label class="sp-control-label"><span >{{ $t('newsletter.emailLabel') }}</span><strong >*</strong></label>
                    <input type="email" sp-type="email" name="sform[email]" class="sp-form-control " :placeholder="$t('newsletter.emailPlaceholder')" sp-tips="%7B%22required%22%3A%22Pflichtfeld%22%2C%22wrong%22%3A%22Falsche%20E-mail%22%7D" autocomplete="on" required="required">
              </div>
                  <div class="sp-field sp-button-container sp-lg" sp-id="sp-3f54eb1e-4ba2-46e1-aeb8-17eb64a3b473">
                    <button id="sp-3f54eb1e-4ba2-46e1-aeb8-17eb64a3b473" class="sp-button">{{ $t('newsletter.submitButton') }}</button>
                  </div>
                </form>
                  </div>
                  </div>
                </div>
              </div>
        
        <p class="newsletter-privacy">{{ $t('newsletter.privacy') }}</p>
      </div>
    </section>

    <LegalFooter />

    <div v-if="showMobilePopup" class="mobile-popup">
      <div class="mobile-popup-content">
        <button class="mobile-popup-close" @click="closeMobilePopup">×</button>
        <p v-html="popupText"></p>
        <a :href="popupLink" target="_blank" rel="noopener" class="demo-link mobile-popup-link">{{ $t('mobilePopup.letsGo') }}</a>
      </div>
    </div>

    <!-- Sticky CTA Button - erscheint nach Partners-Sektion -->
    <div v-if="showStickyButton" class="sticky-cta">
      <div class="sticky-cta-content">
        <a href="https://app.reclaim.ai/m/dawid-paliczuk/15-min-call" target="_blank" rel="noopener" class="sticky-cta-button">
          {{ $t('sticky.ctaButton') }}
        </a>
      </div>
    </div>

    <!-- Instagram In-App Browser Warning -->
    <div v-if="showInstagramWarning" class="instagram-warning">
      <div class="warning-content">
        <div class="warning-icon">📱</div>
        <h3>{{ $t('instagram.warningTitle') }}</h3>
        <p>{{ $t('instagram.warningText') }}</p>
        <button @click="openInExternalBrowser" class="warning-button">
          {{ $t('instagram.openExternal') }}
        </button>
        <button @click="dismissInstagramWarning" class="warning-dismiss">
          {{ $t('instagram.stayHere') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from './sections/Navigation.vue'
import Hero from './sections/Hero.vue'
import Partners from './sections/Partners.vue'
import QRCodeGenerator from './QRCodeGenerator.vue'
import AstrologyQRGenerator from './AstrologyQRGenerator.vue'
import LegalFooter from './legal/LegalFooter.vue'
import { openDeeplink, isInAppBrowser, safeInstagramRedirect } from '../utils/deeplink.js'
import { hasAnalyticsConsent, hasCommunicationConsent, loadSendPulseScript } from '../utils/analyticsConsent.js'

export default {
  name: 'PersonalCoding',
  components: {
    Navigation,
    Hero,
    Partners,
    QRCodeGenerator,
    AstrologyQRGenerator,
    LegalFooter
  },
  data() {
    return {
      activeTooltip: null,
      currentLetter: 'V.',
      typingStage: 0,
      showMobilePopup: false,
      popupText: '',
      popupLink: '',
      showInstagramWarning: false,
      currentInstagramButton: null, // Speichert welcher Instagram-Button geklickt wurde
      showStickyButton: false,
      selectedBranch: null, // Für den interaktiven Quiz
      newsletterEmailPending: '',
      newsletterObserver: null,
      newsletterFormSubmitHandler: null,
      terminCtaClickHandler: null,
      sendPulseObserver: null,
      onCommunicationConsentGranted: null
    }
  },
  computed: {
    newsletterConsentHtml() {
      const privacyLinkHtml = `<a href="/datenschutz" target="_blank">${this.$t('newsletter.privacyLink')}</a>`
      return this.$t('newsletter.consent', { privacyLink: privacyLinkHtml })
    }
  },
  mounted() {
    this.setupNewsletterLazyLoad()
    this.setupNewsletterPixelTracking()
    this.setupTerminCtaTracking()
    this.onCommunicationConsentGranted = () => this.tryLoadSendPulse()
    window.addEventListener('communication-consent-granted', this.onCommunicationConsentGranted)
  },
  unmounted() {
    if (this.onCommunicationConsentGranted) {
      window.removeEventListener('communication-consent-granted', this.onCommunicationConsentGranted)
      this.onCommunicationConsentGranted = null
    }
    if (this.sendPulseObserver) {
      this.sendPulseObserver.disconnect()
      this.sendPulseObserver = null
    }
    if (this.newsletterObserver) {
      this.newsletterObserver.disconnect()
      this.newsletterObserver = null
    }
    if (this.newsletterFormSubmitHandler) {
      const form = document.querySelector('#sp-form-249488 form')
      if (form) {
        form.removeEventListener('submit', this.newsletterFormSubmitHandler)
      }
      this.newsletterFormSubmitHandler = null
    }
    if (this.terminCtaClickHandler) {
      document.removeEventListener('click', this.terminCtaClickHandler, true)
      this.terminCtaClickHandler = null
    }
  },
  methods: {
    tryLoadSendPulse() {
      if (!hasCommunicationConsent() || window.sendPulseLoaded) {
        return
      }

      loadSendPulseScript()
      window.sendPulseLoaded = true
      this.setupNewsletterPixelTracking()
    },
    setupNewsletterLazyLoad() {
      const newsletterSection = document.getElementById('newsletter')
      if (!newsletterSection) {
        return
      }

      this.sendPulseObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.tryLoadSendPulse()
          if (this.sendPulseObserver) {
            this.sendPulseObserver.disconnect()
            this.sendPulseObserver = null
          }
        }
      }, { rootMargin: '200px' })

      this.sendPulseObserver.observe(newsletterSection)

      if (hasCommunicationConsent()) {
        this.tryLoadSendPulse()
      }
    },
    setupTerminCtaTracking() {
      this.terminCtaClickHandler = (event) => {
        if (!(event.target instanceof Element)) {
          return
        }

        const impressumLink = event.target.closest('a[href="/impressum"], a[href$="/impressum"]')
        if (impressumLink) {
          this.firePulsePixelEvent({
            action: 'impressum'
          })
          return
        }

        const bookingLink = event.target.closest('a[href*="app.reclaim.ai/m/dawid-paliczuk/15-min-call"]')
        if (!bookingLink) {
          return
        }

        const sourceContainer = bookingLink.closest('section, .hero-section, .sticky-cta, .partner-card')
        const source = sourceContainer?.id || sourceContainer?.classList?.[0] || 'unknown'
        this.firePulsePixelEvent({
          action: 'termin',
          source
        })
      }

      document.addEventListener('click', this.terminCtaClickHandler, true)
    },
    setupNewsletterPixelTracking() {
      const setup = () => {
        const form = document.querySelector('#sp-form-249488 form')
        const emailInput = document.querySelector('#sp-form-249488 input[name="sform[email]"]')
        const messageContainer = document.querySelector('#sp-form-249488 .sp-message')
        const formContainer = document.querySelector('#sp-form-249488')

        if (!form || !emailInput || !messageContainer || !formContainer) {
          return false
        }

        if (this.newsletterFormSubmitHandler) {
          form.removeEventListener('submit', this.newsletterFormSubmitHandler)
        }

        this.newsletterFormSubmitHandler = () => {
          this.newsletterEmailPending = (emailInput.value || '').trim()
        }
        form.addEventListener('submit', this.newsletterFormSubmitHandler)

        if (this.newsletterObserver) {
          this.newsletterObserver.disconnect()
        }

        this.newsletterObserver = new MutationObserver(() => {
          const messageText = (messageContainer.textContent || '').trim().toLowerCase()
          const hasSuccessClass =
            messageContainer.classList.contains('sp-message-success') ||
            messageContainer.classList.contains('success') ||
            formContainer.classList.contains('sp-success')

          const successKeywords = ['erfolgreich', 'danke', 'thank', 'bestätig', 'confirm']
          const hasSuccessKeyword = successKeywords.some((keyword) => messageText.includes(keyword))

          if ((hasSuccessClass || hasSuccessKeyword) && this.newsletterEmailPending) {
            this.fireNewsletterPixelEvent(this.newsletterEmailPending)
            this.newsletterEmailPending = ''
          }
        })

        this.newsletterObserver.observe(messageContainer, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true
        })

        return true
      }

      if (setup()) {
        return
      }

      let retries = 12
      const retrySetup = setInterval(() => {
        if (setup() || retries <= 0) {
          clearInterval(retrySetup)
        }
        retries -= 1
      }, 500)
    },
    firePulsePixelEvent(payload, attempt = 0) {
      if (!hasAnalyticsConsent()) {
        return
      }

      const spPin = window.SP_PIN
      if (spPin && typeof spPin.fireEvent === 'function') {
        spPin.fireEvent('077437c1-6270-4c88-9cb9-1096d8f75094', payload)
        return
      }

      if (attempt < 10) {
        setTimeout(() => {
          this.firePulsePixelEvent(payload, attempt + 1)
        }, 300)
      }
    },
    fireNewsletterPixelEvent(mail, attempt = 0) {
      this.firePulsePixelEvent({
        mail,
        action: 'newsletter'
      }, attempt)
    },
    showTooltip(letter) {
      if (window.innerWidth > 768) {  // Nur für Desktop
        this.activeTooltip = letter;
      }
    },
    hideTooltip() {
      if (window.innerWidth > 768) {  // Nur für Desktop
        this.activeTooltip = null;
      }
    },
    selectBranch(branch) {
      this.selectedBranch = branch;
      this.firePulsePixelEvent({
        action: 'quiz',
        step: 'branch_select',
        branch
      })
    },
    resetQuiz() {
      this.selectedBranch = null;
      this.firePulsePixelEvent({
        action: 'quiz',
        step: 'reset'
      })
      // Scroll zu "Wähle deine Branche:" nach dem Reset
      this.$nextTick(() => {
        const element = document.getElementById('quiz-branch-selection');
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      });
    },
    toggleTooltip(letter) {
      if (window.innerWidth <= 768) {  // Nur für Mobile
        this.activeTooltip = this.activeTooltip === letter ? null : letter;
      }
    },
    // Entfernt: handleScroll und scrollToSection
    startTypingAnimation() {
      setInterval(() => {
        switch (this.typingStage) {
          case 0: // Show V
            this.currentLetter = 'V.';
            break;
          case 1: // Delete
            this.currentLetter = '';
            break;
          case 2: // Show W
            this.currentLetter = 'W.';
            break;
          case 3: // Delete
            this.currentLetter = '';
            break;
        }
        this.typingStage = (this.typingStage + 1) % 4;
      }, 1000); // Adjust timing as needed
    },

    openDeeplinkDemo(type) {
      const links = {
        horoskop: 'https://ig.me/m/_innerwisdom_?ref=68f0fc130bb58a162209ee6e',
        meditation: 'https://t.me/Renato_achtsamkeit_bot?start=68752fad25aad4692c0de436',
        yoga: 'https://link.paliczuk.de/yoga'
      };

      const url = links[type];
      if (url) {
        openDeeplink(url, {
          fallback: true,
          target: '_blank'
        });
      }
    },

    openInstagramButton(type) {
      // Check if in Instagram in-app browser
      if (isInAppBrowser()) {
        this.currentInstagramButton = type; // Speichere den Button-Typ
        this.showInstagramWarning = true;
        return;
      }
      
      // If not in Instagram, open normally
      this.openDeeplinkDemo(type);
    },
    openMobilePopup(type) {
      const links = {
        horoskop: 'https://ig.me/m/_innerwisdom_?ref=68f0fc130bb58a162209ee6e',
        meditation: 'https://t.me/Renato_achtsamkeit_bot?start=68752fad25aad4692c0de436',
        yoga: 'https://link.paliczuk.de/yoga'
      };

      const url = links[type];
      if (url) {
        // Verwende das safari:// URL-Schema für iOS
        if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
          window.location.href = `safari-https://link.paliczuk.de/${type}`;
        } else {
          window.open(url, '_blank');
        }
      }
    },
    closeMobilePopup() {
      this.showMobilePopup = false;
    },
         openInExternalBrowser() {
       // Get the Instagram link based on the current button
       const links = {
         horoskop: 'https://ig.me/m/_innerwisdom_?ref=68f0fc130bb58a162209ee6e',
         meditation: 'https://t.me/Renato_achtsamkeit_bot?start=68752fad25aad4692c0de436',
         yoga: 'https://link.paliczuk.de/yoga'
       };

       const targetUrl = this.currentInstagramButton ? links[this.currentInstagramButton] : window.location.href;
       
       // Use the safe Instagram redirect function with the specific link
       safeInstagramRedirect({
         fallbackUrl: targetUrl,
         delay: 1500
       });
     },
    dismissInstagramWarning() {
      this.showInstagramWarning = false;
      this.currentInstagramButton = null; // Reset the button type
    }
  }
}
</script>

<style scoped>
/* PersonalCoding Theme Support */
.section {
  background: var(--bg-primary);
  color: var(--text-primary);
}

.section-intro {
  color: var(--text-secondary);
}

.section-intro h2 {
  color: var(--text-primary);
}

.section-intro h3 {
  color: var(--text-primary);
}

.section-intro p {
  color: var(--text-secondary);
}

.solution-statement {
  color: var(--text-primary);
}

.fade-text {
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.bonus-card {
  background: var(--glass-bg);
  backdrop-filter: blur(16px);
  border: 1px solid var(--border-color);
}

.bonus-stats {
  color: var(--text-primary);
}

.benefits-list {
  color: var(--text-secondary);
}

.benefits-title {
  color: var(--text-primary);
}

.benefits-list li {
  color: var(--text-secondary);
}

.alternatives-comparison {
  background: var(--glass-bg);
  backdrop-filter: blur(16px);
  border: 1px solid var(--border-color);
}

.comparison-column {
  background: var(--glass-bg);
  backdrop-filter: blur(16px);
  border: 1px solid var(--border-color);
}

.timeline-item {
  background: transparent;
  backdrop-filter: none;
  border: none;
}

.timeline-content {
  color: var(--text-primary);
}

.timeline-date {
  color: var(--text-primary);
}

.timeline-content p {
  color: var(--text-secondary);
}

.instagram-chat-demo {
  background: var(--glass-bg);
  backdrop-filter: blur(16px);
  border: 1px solid var(--border-color);
}

.ai-demo-label {
  margin: 0;
  padding: 10px 16px;
  font-size: 0.78rem;
  line-height: 1.4;
  color: #00ffb3;
  background: rgba(0, 255, 179, 0.08);
  border-bottom: 1px solid rgba(0, 255, 179, 0.2);
  text-align: center;
}

/* Chat Marketing Theme Tweaks */
.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--glass-bg);
  border-bottom: 1px solid var(--border-color);
}

.chat-messages {
  background: var(--bg-primary);
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
}

.message.incoming .message-bubble {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.message.outgoing .message-bubble {
  background: var(--accent-blue);
  color: var(--text-primary);
}

.chat-cta {
  border-top: 1px solid var(--border-color);
  background: var(--glass-bg);
}

.chat-cta-button {
  display: inline-block;
  background: var(--gradient-primary);
  color: var(--text-primary);
  border: none;
  border-radius: 12px;
  padding: 12px 20px;
  font-weight: 700;
  text-align: center;
}

.chat-name {
  color: var(--text-primary);
}

.chat-status {
  color: var(--text-secondary);
}

.chat-scroll-cta {
  color: var(--text-secondary);
}

.message-bubble {
  background: var(--glass-bg);
  backdrop-filter: blur(16px);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
}

.message-time {
  color: var(--text-secondary);
}

.highlight-message .message-bubble {
  background: var(--gradient-primary);
  color: var(--text-primary);
}

.quiz-container {
  background: var(--glass-bg);
  backdrop-filter: blur(16px);
  border: 1px solid var(--border-color);
}

.quiz-instruction {
  color: var(--text-primary);
}

.branch-badge {
  background: var(--glass-bg);
  backdrop-filter: blur(16px);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
}

.branch-example {
  color: var(--text-primary);
}

.branch-example h6 {
  color: var(--text-primary);
}

.branch-example p {
  color: var(--text-secondary);
}

.branch-example ul {
  color: var(--text-secondary);
}

.branch-example li {
  color: var(--text-secondary);
}

.reset-btn {
  background: var(--glass-bg);
  backdrop-filter: blur(16px);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
}

.newsletter-section {
  background: var(--gradient-secondary);
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
}

.newsletter-title {
  color: var(--text-primary);
}

.newsletter-text {
  color: var(--text-secondary);
}

.newsletter-benefits {
  color: var(--text-secondary);
}

.newsletter-benefits li {
  color: var(--text-secondary);
}

.newsletter-privacy {
  color: var(--text-secondary);
}

.final-cta-text {
  color: var(--text-secondary);
}

.cta-guarantee {
  color: var(--accent-green, #00ffb3);
}
</style>

<style scoped>

/* USP section visual refresh: rounded gradient-pill cards */
#usp .offer-grid {
  gap: 24px;
}

#usp .offer-block {
  /* Match demo-card styling */
  background: var(--glass-bg);
  border-radius: 20px;
  padding: 40px;
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
}

#usp .offer-block:hover {
  transform: translateY(-5px);
  background: var(--glass-bg);
  border-color: var(--accent-green, rgba(0, 255, 179, 0.3));
}

#usp .block-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
}

#usp .block-icon { font-size: 3rem; margin-bottom: 15px; }

#usp .block-header h3 {
  color: var(--text-primary);
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
}

#usp .block-subtitle {
  color: var(--text-secondary);
  font-size: 0.95rem;
  text-align: center;
}

#usp .benefit-item {
  padding: 12px 14px;
  background: var(--glass-bg);
  backdrop-filter: blur(12px);
  border-radius: 12px;
  border: 1px solid var(--border-color);
  box-shadow: 0 10px 28px var(--overlay-light);
}

#usp .benefit-icon { font-size: 1.2rem; }

#usp .block-cta .cta-button {
  background: var(--gradient-blue-purple);
  border: 1px solid rgba(255,255,255,0.12);
  box-shadow: 0 10px 28px rgba(102, 126, 234, 0.35);
}

@media (max-width: 768px) {
  #usp .offer-block { padding: 26px 20px; border-radius: 20px; }
  #usp .block-icon { width: 48px; height: 48px; font-size: 1.4rem; }
  #usp .block-header h3 { font-size: 1.25rem; }
}

/* Problem Section Styles */
.problems-section {
  padding: 80px 20px;
  background: linear-gradient(180deg, rgba(255, 107, 107, 0.05), rgba(255, 165, 0, 0.05));
  text-align: center;
}

.problems-content {
  max-width: 1200px;
  margin: 0 auto;
}

.problems-section h2 {
  font-size: 3rem;
  margin-bottom: 20px;
  color: var(--text-primary);
  text-align: center;
}

.section-intro {
  font-size: 1.2rem;
  color: var(--text-secondary);
  margin-bottom: 50px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.problems-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  margin-bottom: 50px;
}

.problem-card {
  background: var(--glass-overlay);
  border-radius: 20px;
  padding: 30px;
  border: 1px solid rgba(255, 107, 107, 0.2);
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
}

.problem-card:hover {
  transform: translateY(-5px);
  background: var(--card-bg);
  border-color: rgba(255, 107, 107, 0.4);
}

.problem-icon {
  font-size: 3rem;
  margin-bottom: 20px;
}

.problem-card h3 {
  font-size: 1.5rem;
  margin-bottom: 15px;
  color: var(--accent-red);
}

.problem-card p {
  color: var(--text-secondary);
  line-height: 1.6;
}

.problems-cta {
  text-align: center;
  margin-top: 40px;
}

.cta-intro {
  font-size: 1.1rem;
  color: var(--text-secondary);
  margin-bottom: 20px;
}

.demo-cta-text {
  font-size: 1.1rem;
  color: var(--text-secondary);
  margin-bottom: 20px;
}

.final-cta {
  text-align: center;
  margin: 40px 0;
}

.final-cta-text {
  font-size: 1.2rem;
  color: var(--text-secondary);
  margin-bottom: 25px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.cta-guarantee {
  font-size: 0.9rem;
  color: var(--accent-green, #00ffb3);
  margin-top: 15px;
  font-weight: 500;
}

/* Newsletter Section */
.newsletter-section {
  padding: 60px 20px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.newsletter-content {
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
}

@media (min-width: 769px) {
  .newsletter-content {
    text-align: center;
  }
}

.newsletter-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 15px;
  line-height: 1.2;
}

.newsletter-text {
  font-size: 1.1rem;
  color: var(--text-secondary);
  margin-bottom: 20px;
}

@media (min-width: 769px) {
  .newsletter-text {
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
    text-align: left;
  }
}

.newsletter-benefits {
  list-style: none;
  padding: 0;
  margin: 25px 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-left: auto;
  margin-right: auto;
  max-width: 600px;
}

@media (min-width: 769px) {
  .newsletter-benefits {
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }
}

.newsletter-benefits li {
  font-size: 1rem;
  color: var(--text-secondary);
  margin-bottom: 10px;
  padding-left: 0;
}

.newsletter-form-wrapper {
  max-width: 500px;
  margin: 30px auto;
}

@media (min-width: 769px) {
  .newsletter-form-wrapper {
    margin: 30px auto;
  }
}

.newsletter-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.newsletter-input {
  background: var(--bg-primary);
  border: 2px solid var(--border-color);
  border-radius: 25px;
  padding: 12px 20px;
  font-size: 15px;
  width: 100%;
  transition: all 0.3s ease;
  color: var(--text-primary);
}

.newsletter-input:focus {
  outline: none;
  border-color: var(--accent-primary);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.newsletter-checkboxes {
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: left;
}

.newsletter-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.85rem;
  color: var(--text-secondary);
  cursor: pointer;
}

.newsletter-checkbox input[type="checkbox"] {
  margin-top: 3px;
  cursor: pointer;
}

.newsletter-checkbox a {
  color: var(--accent-primary);
  text-decoration: underline;
}

.newsletter-submit-btn {
  background: var(--gradient-blue-purple);
  color: var(--text-primary);
  border: none;
  padding: 16px 40px;
  font-size: 1.1rem;
  font-weight: 700;
  border-radius: 12px;
  cursor: pointer;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
  width: 100%;
}

.newsletter-submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 35px rgba(102, 126, 234, 0.6);
}

        /* SendPulse Form Styles - Angepasst an Website Design */
        .sp-force-hide { 
          display: none;
        }

        .sp-form[sp-id="249488"] { 
          display: block; 
          background: rgba(15, 16, 35, 0.95); 
          padding: 30px; 
          width: 100%; 
          max-width: 600px; 
          border-radius: 16px; 
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif; 
          background-repeat: no-repeat; 
          background-position: center; 
          background-size: auto;
          border: 1px solid rgba(102, 126, 234, 0.2);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
          backdrop-filter: blur(20px);
        }

        /* Custom Checkbox Design */
        .sp-form[sp-id="249488"] .sp-checkbox-option {
          position: relative;
        }

        .sp-form[sp-id="249488"] .sp-checkbox-option input[type="checkbox"] {
          position: absolute;
          opacity: 0;
          cursor: pointer;
          height: 0;
          width: 0;
        }

        .sp-form[sp-id="249488"] .sp-checkbox-option label {
          position: relative;
          padding-left: 35px;
          cursor: pointer;
          display: flex;
          align-items: flex-start;
          gap: 0;
        }

        .sp-form[sp-id="249488"] .sp-checkbox-option label::before {
          content: '';
          position: absolute;
          left: 0;
          top: 2px;
          width: 20px;
          height: 20px;
          border: 2px solid rgba(102, 126, 234, 0.5);
          border-radius: 6px;
          background: rgba(30, 32, 50, 0.8);
          transition: all 0.3s ease;
        }

        .sp-form[sp-id="249488"] .sp-checkbox-option label::after {
          content: '❤️';
          position: absolute;
          left: 3px;
          top: 2px;
          font-size: 14px;
          opacity: 0;
          transition: all 0.3s ease;
          transform: scale(0.5);
        }

        .sp-form[sp-id="249488"] .sp-checkbox-option input[type="checkbox"]:checked + label::before {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
        }

        .sp-form[sp-id="249488"] .sp-checkbox-option input[type="checkbox"]:checked + label::after {
          opacity: 1;
          transform: scale(1);
        }

        .sp-form[sp-id="249488"] .sp-checkbox-option label:hover::before {
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        /* Mobile Responsive Checkbox */
        @media (max-width: 768px) {
          .sp-form[sp-id="249488"] .sp-checkbox-option label {
            padding-left: 40px;
            font-size: 13px;
            line-height: 1.4;
          }

          .sp-form[sp-id="249488"] .sp-checkbox-option label::before {
            width: 22px;
            height: 22px;
            top: 1px;
          }

          .sp-form[sp-id="249488"] .sp-checkbox-option label::after {
            left: 4px;
            top: 3px;
            font-size: 16px;
          }
        }

        @media (max-width: 430px) {
          .sp-form[sp-id="249488"] .sp-checkbox-option label {
            padding-left: 35px;
            font-size: 12px;
            line-height: 1.3;
          }

          .sp-form[sp-id="249488"] .sp-checkbox-option label::before {
            width: 20px;
            height: 20px;
            top: 2px;
          }

          .sp-form[sp-id="249488"] .sp-checkbox-option label::after {
            left: 0px;
            top: 2px;
            font-size: 14px;
          }
        }

        .sp-form[sp-id="249488"] .sp-form-fields-wrapper { 
          margin: 0 auto; 
          width: 100%;
          max-width: 500px;
        }

        .sp-form[sp-id="249488"] .sp-form-control { 
          background: rgba(30, 32, 50, 0.8); 
          border: 2px solid rgba(102, 126, 234, 0.3); 
          color: #e2e8f0;
          font-size: 16px; 
          padding: 14px 20px; 
          border-radius: 12px; 
          height: auto; 
          width: 100%;
          transition: all 0.3s ease;
        }

        .sp-form[sp-id="249488"] .sp-form-control:focus {
          outline: none;
          border-color: #667eea;
          background: rgba(30, 32, 50, 1);
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
        }

        .sp-form[sp-id="249488"] .sp-form-control::placeholder {
          color: #94a3b8;
        }

        .sp-form[sp-id="249488"] .sp-field label { 
          color: #e2e8f0; 
          font-size: 14px; 
          font-style: normal; 
          font-weight: 600;
          margin-bottom: 8px;
          display: block;
        }

        .sp-form[sp-id="249488"] .sp-field {
          margin-bottom: 20px;
        }

        .sp-form[sp-id="249488"] .sp-checkbox-option label {
          color: #cbd5e1;
          font-size: 14px;
          line-height: 1.5;
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }

        .sp-form[sp-id="249488"] .sp-checkbox-option input[type="checkbox"] {
          margin-top: 2px;
          flex-shrink: 0;
        }

        .sp-form[sp-id="249488"] .sp-checkbox-option a {
          color: #667eea;
          text-decoration: underline;
        }

        .sp-form[sp-id="249488"] .sp-button-messengers { 
          border-radius: 12px;
        }

        .sp-form[sp-id="249488"] .sp-button { 
          border-radius: 12px; 
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); 
          color: #ffffff; 
          width: 100%; 
          font-weight: 700; 
          font-size: 16px;
          font-style: normal; 
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif; 
          box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
          border: none;
          padding: 16px 24px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
        }

        .sp-form[sp-id="249488"] .sp-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 35px rgba(102, 126, 234, 0.6);
        }

        .sp-form[sp-id="249488"] .sp-button-container { 
          text-align: center; 
          width: auto;
          margin-top: 10px;
        }

        /* Newsletter Header Styling */
        .sp-form[sp-id="249488"] .sp-field[sp-id="sp-8e0d365e-ef9e-49a2-a6af-086dc7a8f2f2"] p {
          color: #e2e8f0;
          text-align: center;
          margin-bottom: 20px;
        }

        .sp-form[sp-id="249488"] .sp-field[sp-id="sp-8e0d365e-ef9e-49a2-a6af-086dc7a8f2f2"] strong {
          color: #ffffff;
        }

        .sp-form[sp-id="249488"] .sp-field[sp-id="sp-8e0d365e-ef9e-49a2-a6af-086dc7a8f2f2"] span {
          color: #cbd5e1;
        }

        /* Newsletter Benefits List - Links zentriert */
        .sp-form[sp-id="249488"] .sp-field[sp-id="sp-8e0d365e-ef9e-49a2-a6af-086dc7a8f2f2"] p:last-child {
          text-align: left;
          margin-left: auto;
          margin-right: auto;
          max-width: 300px;
        }

.newsletter-privacy {
  font-size: 0.85rem;
  color: var(--text-muted);
  margin-top: 10px;
}

@media (max-width: 768px) {
  .newsletter-title {
    font-size: 1.6rem;
  }
  
  .newsletter-text {
    font-size: 1rem;
  }
  
  .newsletter-benefits {
    text-align: center;
  }
  
  .newsletter-form-wrapper {
    max-width: 100%;
    padding: 0 10px;
  }
  
  .newsletter-input {
    font-size: 14px;
    padding: 10px 16px;
  }
  
  .newsletter-submit-btn {
    font-size: 1rem;
    padding: 14px 30px;
  }
}

@media (max-width: 768px) {
  .problems-section h2 {
    font-size: 2.5rem;
  }
  
  .problems-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .problem-card {
    padding: 25px 20px;
  }
}

/* Scroll-Performance Optimierung */
/* removed custom scroll padding to restore native behavior */



/* Angebotsblöcke Styling */
.offer-blocks {
  padding: 80px 20px;
  background: var(--gradient-secondary);
}

.offer-blocks .container {
  max-width: 1200px;
  margin: 0 auto;
}

.offer-blocks h2 {
  text-align: center;
  font-size: 3rem;
  margin-bottom: 50px;
  background: var(--gradient-green-blue);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.offer-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 40px;
}

.offer-block {
  background: var(--glass-overlay);
  border-radius: 20px;
  padding: 40px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.offer-block:hover {
  transform: translateY(-5px);
  background: var(--card-bg);
  border-color: rgba(0, 255, 179, 0.3);
}

.block-header {
  text-align: center;
  margin-bottom: 30px;
}

.block-icon {
  font-size: 4rem;
  margin-bottom: 20px;
}

.block-header h3 {
  font-size: 1.8rem;
  color: var(--text-primary);
  line-height: 1.4;
  margin: 0;
}

.block-benefits {
  margin-bottom: 30px;
}

.benefit-item {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 20px;
  padding: 16px 18px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  background: var(--glass-bg);
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 24px var(--overlay-light);
  transition: background 0.3s ease;
}

.benefit-item:hover {
  background: var(--glass-overlay);
  border-color: var(--accent-green, rgba(0, 255, 179, 0.3));
  box-shadow: 0 12px 32px var(--overlay-medium);
}

.benefit-icon {
  font-size: 1.3rem;
  min-width: 30px;
}

.benefit-item span:last-child {
  color: var(--text-secondary);
  font-size: 1rem;
  line-height: 1.5;
}

.block-cta {
  text-align: center;
}

.block-cta .cta-button {
  display: inline-block;
  background: var(--gradient-purple-pink);
  color: var(--text-primary);
  padding: 15px 30px;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(139, 92, 246, 0.3);
}

.block-cta .cta-button:hover {
  background: var(--gradient-pink-purple);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(139, 92, 246, 0.4);
}

@media (max-width: 768px) {
  .offer-blocks {
    padding: 60px 20px;
  }

  .offer-blocks h2 {
    font-size: 2.5rem;
  }

  .offer-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .offer-block {
    padding: 30px;
  }

  .block-header h3 {
    font-size: 1.5rem;
  }
}

/* Removed duplicate body rule - handled in App.vue */

.vision-tag {
  display: inline-block;
  background: var(--gradient-green-blue);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  margin: 20px 0;
  font-size: 1.1rem;
  animation: pulse-light 2s infinite;
}

/* Timeline-Styling für zentrierte Punkte */
.timeline {
  position: relative;
  margin: 40px 0;
  padding: 0;
}

.timeline-item {
  position: relative;
  z-index: 2; /* keep cards above the line caps but not hiding the line */
  display: flex;
  margin-bottom: 40px;
  min-height: 70px;
}

.timeline-content {
  width: 45%;
  background: var(--card-bg);
  padding: 20px;
  border-radius: 10px;
  position: relative;
  box-shadow: 0 4px 10px var(--overlay-light);
}

.timeline-item:nth-child(odd) .timeline-content {
  margin-right: auto;
  margin-left: 60px;
  text-align: left;
}

.timeline-item:nth-child(even) .timeline-content {
  margin-left: auto;
  text-align: right;
}

.timeline-date {
  display: inline-block;
  background: var(--gradient-green-blue);
  color: white;
  padding: 5px 15px;
  border-radius: 20px;
  font-weight: 600;
  margin-bottom: 10px;
}

/* Responsive Timeline */
@media (max-width: 768px) {
  .timeline-item {
    flex-direction: column;
  }
  
  .timeline-item:nth-child(odd) .timeline-content,
  .timeline-item:nth-child(even) .timeline-content {
    width: 95%;
    margin-left: 20px;
    margin-right: auto;
    text-align: left;
  }
}

@keyframes pulse-light {
  0% {
    box-shadow: 0 0 0 0 rgba(182, 36, 255, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(182, 36, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(182, 36, 255, 0);
  }
}

.tagline {
  font-size: 1.8rem;
  margin: 15px 0;
  background: var(--gradient-green-blue);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 600;
  letter-spacing: 0.5px;
  animation: fadeIn 1.5s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.tooltip {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(182, 36, 255, 0.95);
  color: white;
  padding: 15px;
  font-size: 0.9rem;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
}

.tooltip-inner {
  max-width: 90%;
}

.tooltip-inner p {
  margin-bottom: 8px;
  text-align: left;
  line-height: 1.4;
}

.tooltip.active {
  opacity: 1;
  visibility: visible;
}

/* removed vision-section styles */

.vision-content {
  max-width: 900px;
  margin: 0 auto;
}

/* removed vision-section heading styles */

.vision-statement {
  font-size: 1.2rem;
  line-height: 1.8;
}

.vision-statement p {
  margin-bottom: 20px;
}

.vision-goals {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  margin: 40px 0;
}

.vision-goal {
  background: var(--glass-overlay);
  border-radius: 15px;
  padding: 25px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.vision-goal:hover {
  transform: translateY(-5px);
  background: var(--card-bg);
  border-color: rgba(0, 255, 179, 0.3);
}

.goal-icon {
  font-size: 2.5rem;
  margin-bottom: 15px;
}

.vision-conclusion {
  font-size: 1.3rem;
  font-weight: 500;
  margin-top: 30px;
}

.cta-subtext {
  margin-top: 15px;
  opacity: 0.8;
  font-size: 1rem;
}

.second-tier-heading {
  margin-top: 60px;
  margin-bottom: 30px;
  font-size: 1.8rem;
  text-align: center;
  background: var(--gradient-green-blue);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.specialization-benefits li {
  margin-bottom: 25px;
  display: flex;
  align-items: flex-start;
}

.benefit-icon {
  font-size: 1.5rem;
  margin-right: 15px;
  min-width: 35px;
}

.benefit-content {
  display: flex;
  flex-direction: column;
}

.benefit-content strong {
  display: block;
  margin-bottom: 8px;
  font-size: 1.25rem;
  color: var(--text-primary);
}

.benefit-description {
  color: var(--text-secondary);
  font-size: 1rem;
  line-height: 1.6;
}

.benefits-list {
  list-style: none;
  padding: 0;
  margin-top: 25px;
}

.two-cards-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  margin-bottom: 50px;
}

@media (max-width: 768px) {
  .vision-goals {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  /* removed vision-section responsive styles */

  .two-cards-grid {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-bottom: 30px;
  }
}

.mobile-hint-text {
  display: none;
  font-size: 1rem;
  color: var(--text-primary);
  margin-top: 8px;
  background: var(--overlay-light);
  border-radius: 8px;
  padding: 8px 12px;
  text-align: center;
}
@media (max-width: 768px) {
  .mobile-hint-text {
    display: block;
  }
}

.mobile-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: var(--overlay-dark);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.mobile-popup-content {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border-radius: 18px;
  padding: 32px 24px 24px 24px;
  max-width: 90vw;
  box-shadow: 0 8px 32px rgba(0,0,0,0.25);
  text-align: center;
  position: relative;
}
.mobile-popup-close {
  position: absolute;
  top: 12px;
  right: 16px;
  font-size: 2rem;
  color: var(--accent-purple);
  background: none;
  border: none;
  cursor: pointer;
}
.mobile-popup-content p {
  font-size: 1.15rem;
  margin: 0;
}
@media (min-width: 769px) {
  .mobile-popup {
    display: none;
  }
}

.mobile-popup-link {
  display: inline-block;
  margin-top: 24px;
  background: var(--gradient-purple-pink);
  color: var(--text-primary);
  padding: 12px 32px;
  border-radius: 25px;
  text-decoration: none;
  font-size: 1.2rem;
  font-weight: 600;
  transition: background 0.3s;
}
.mobile-popup-link:hover {
  background: var(--gradient-pink-purple);
}

.desktop-disclaimer {
  display: none;
  font-size: 1.05rem;
  color: var(--text-primary);
  margin-top: 10px;
  background: var(--overlay-light);
  border-radius: 8px;
  padding: 8px 12px;
  text-align: center;
}
@media (min-width: 769px) {
  .desktop-disclaimer {
    display: block;
  }
}

.desktop-only {
  display: block !important;
}
@media (max-width: 768px) {
  .desktop-only {
    display: none !important;
  }
}

.desktop-only .desktop-disclaimer {
  display: block !important;
}

.qr-code {
  margin-top: 20px;
}

.qr-code img {
  width: 150px;
  height: auto;
}

@media (max-width: 768px) {
  .qr-code {
    display: none !important;
  }
}

/* Sticky CTA Button */
.sticky-cta {
  position: fixed;
  bottom: calc(20px + env(safe-area-inset-bottom));
  right: 20px;
  z-index: 1000;
  animation: slideInUp 0.5s ease-out;
}

.sticky-cta-content {
  background: var(--gradient-blue-purple);
  border-radius: 50px;
  padding: 0;
  box-shadow: 
    0 10px 30px rgba(102, 126, 234, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset;
  transition: all 0.3s ease;
}

.sticky-cta-content:hover {
  transform: translateY(-2px);
  box-shadow: 
    0 15px 40px rgba(102, 126, 234, 0.6),
    0 0 0 1px rgba(255, 255, 255, 0.2) inset;
}

.sticky-cta-button {
  display: block;
  color: var(--text-primary);
  text-decoration: none;
  padding: 12px 24px;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.9rem;
  white-space: nowrap;
  transition: all 0.3s ease;
  user-select: none;
  -webkit-user-select: none;
}

.sticky-cta-button:hover {
  color: var(--text-primary);
  text-decoration: none;
}

@keyframes slideInUp {
  from {
    transform: translateY(100px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Mobile Anpassungen */
@media (max-width: 768px) {
  .sticky-cta {
    bottom: 15px;
    right: 15px;
  }
  
  .sticky-cta-button {
    padding: 10px 20px;
    font-size: 0.8rem;
  }
}

/* Comparison Section Styles */
.comparison-section {
  margin: 30px 0;
  padding: 25px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 15px;
  border: 1px solid rgba(0, 255, 179, 0.2);
}

.comparison-header h4 {
  text-align: center;
  margin-bottom: 20px;
  font-size: 1.3rem;
  background: var(--gradient-green-blue);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.comparison-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.comparison-column {
  background: var(--glass-overlay);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.comparison-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 15px;
  text-align: center;
  color: var(--text-primary);
}

.comparison-stats {
  display: flex;
  justify-content: space-around;
  margin-bottom: 15px;
}

.stat-highlight {
  text-align: center;
}

.stat-highlight .stat-number {
  display: block;
  font-size: 1.4rem;
  font-weight: bold;
  color: var(--accent-green, #00ffb3);
  margin-bottom: 5px;
}

.stat-highlight .stat-label {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
}

.comparison-benefits {
  list-style: none;
  padding: 0;
  margin: 0;
}

.comparison-benefits li {
  margin-bottom: 8px;
  font-size: 0.9rem;
  color: var(--text-secondary);
}

/* Alternatives Comparison Styles */
.alternatives-comparison {
  margin: 30px 0;
  padding: 25px;
}

.alternatives-comparison h4 {
  text-align: center;
  margin-bottom: 20px;
  font-size: 1.3rem;
  background: var(--gradient-green-blue);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.alternatives-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.alternative-column {
  background: var(--glass-overlay);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.alternative-column:hover {
  transform: translateY(-3px);
  background: var(--card-bg);
}

.alternative-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 15px;
  text-align: center;
  color: var(--text-primary);
}

.alternative-features {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.feature-item.positive {
  background: rgba(0, 255, 179, 0.1);
  border: 1px solid rgba(0, 255, 179, 0.2);
}

.feature-item.negative {
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.2);
}

.feature-icon {
  font-size: 1rem;
  min-width: 20px;
}

.feature-text {
  font-size: 0.85rem;
  color: var(--text-secondary);
  line-height: 1.4;
}

@media (max-width: 768px) {
  .comparison-grid,
  .alternatives-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  
  .comparison-section,
  .alternatives-comparison {
    padding: 20px;
  }
  
  .comparison-stats {
    flex-direction: column;
    gap: 10px;
  }
  
  .stat-highlight {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .stat-highlight .stat-number {
    margin-bottom: 0;
  }
}

.demos {
  padding: 80px 20px;
  background: linear-gradient(180deg, rgba(0, 255, 179, 0.1), rgba(182, 36, 255, 0.1));
  text-align: center;
}

.solution-statement {
  font-size: 2.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 auto 20px;
  max-width: 800px;
  line-height: 1.3;
  animation: fadeInUp 1s ease-out;
  padding: 0 20px;
}

@media (max-width: 768px) {
  .solution-statement {
    font-size: 1.8rem;
    max-width: 100%;
    line-height: 1.4;
    padding: 0 15px;
  }
}

.fade-text {
  background: var(--gradient-blue-purple);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 600;
  position: relative;
  display: inline-block;
  animation: gradientShift 3s ease infinite;
  background-size: 200% 200%;
}

@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.fade-heading {
  background: var(--gradient-green-blue);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: fadeInUp 1s ease-out;
  font-size: 2.5rem;
  font-weight: 600;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.target-audience {
  margin: 20px 0;
  padding: 15px;
  background: rgba(0, 255, 179, 0.05);
  border-radius: 10px;
  border: 1px solid rgba(0, 255, 179, 0.2);
  text-align: center;
}

.target-audience p {
  margin: 8px 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.target-audience strong {
  color: var(--accent-green, #00ffb3);
  font-weight: 600;
}

.target-audience ul {
  list-style: none;
  padding: 0;
  margin: 15px 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  justify-content: center;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.target-audience li {
  background: rgba(0, 255, 179, 0.1);
  padding: 8px 15px;
  border-radius: 20px;
  color: var(--accent-green, #00ffb3);
  font-weight: 500;
  font-size: 0.85rem;
  border: 1px solid rgba(0, 255, 179, 0.3);
}

.usp-cards {
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.usp-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--glass-overlay);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.usp-card:hover {
  background: var(--card-bg);
  border-color: rgba(0, 255, 179, 0.3);
  transform: translateX(5px);
}

.usp-emoji {
  font-size: 1.2rem;
  flex-shrink: 0;
}

.usp-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
}

.usp-text {
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.4;
}

.usp-subtitle {
  color: var(--text-muted);
  font-size: 0.8rem;
  font-weight: 400;
  line-height: 1.3;
  font-style: italic;
}

.demo-card {
  background: var(--glass-overlay);
  border-radius: 20px;
  padding: 40px;
  margin: 20px auto;
  max-width: 600px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.demo-card:hover {
  transform: translateY(-5px);
  background: var(--card-bg);
  border-color: rgba(0, 255, 179, 0.3);
}

.user-experience {
  margin: 25px 0;
  padding: 20px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 15px;
  text-align: left;
}

.experience-step {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 15px;
  padding: 10px;
  border-left: 3px solid transparent;
  transition: all 0.3s ease;
}

.experience-step:hover {
  border-left-color: var(--accent-green);
  background: var(--glass-overlay);
  border-radius: 8px;
}

.step-emoji {
  font-size: 1.5rem;
  min-width: 40px;
  text-align: center;
}

.step-text {
  flex: 1;
}

.step-text strong {
  color: var(--accent-green, #00ffb3);
  display: block;
  margin-bottom: 5px;
}

.demo-highlight {
  margin-top: 20px;
  padding: 15px;
  background: rgba(182, 36, 255, 0.1);
  border-radius: 10px;
  border: 1px solid rgba(182, 36, 255, 0.3);
}

.demo-highlight p {
  margin: 0;
  font-style: italic;
  color: var(--text-secondary);
}

@media (max-width: 768px) {
  .demo-card {
    padding: 30px 20px;
  }

  .target-audience {
    margin: 14px 0;
    padding: 12px;
  }

  .target-audience ul {
    margin: 10px 0;
    gap: 10px;
  }

  .target-audience li {
    padding: 7px 10px;
    font-size: 0.8rem;
    line-height: 1.2;
  }
  
  .experience-step:hover {
    border-left-color: transparent;
    background: transparent;
  }
}

.testimonials {
  padding: 80px 20px;
  background: linear-gradient(180deg, var(--overlay-dark), var(--overlay-medium));
}

.testimonials h2 {
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 15px;
  background: var(--gradient-green-blue);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.section-subtitle {
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 50px;
  font-size: 1.2rem;
}

.testimonials-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
}

.testimonial-card {
  background: var(--glass-overlay);
  border-radius: 20px;
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.testimonial-card:hover {
  transform: translateY(-5px);
  background: var(--card-bg);
  border-color: rgba(0, 255, 179, 0.3);
}

.testimonial-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.testimonial-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: var(--gradient-green-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
}

.avatar-placeholder {
  font-size: 2rem;
}

.testimonial-meta h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.2rem;
}

.testimonial-meta p {
  margin: 5px 0 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
}

.testimonial-stats {
  display: flex;
  justify-content: space-around;
  margin: 20px 0;
  padding: 15px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
}

.stat {
  text-align: center;
}

.stat-number {
  display: block;
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--accent-green, #00ffb3);
  margin-bottom: 5px;
}

.stat-label {
  font-size: 0.9rem;
  color: var(--text-muted);
}

.testimonial-text {
  color: var(--text-secondary);
  font-style: italic;
  line-height: 1.6;
  margin: 0;
}

@media (max-width: 768px) {
  .testimonials {
    padding: 60px 20px;
  }

  .testimonials h2 {
    font-size: 2rem;
  }

  .testimonials-grid {
    grid-template-columns: 1fr;
  }

  .testimonial-card {
    padding: 20px;
  }
}

.trusted-by {
  padding: 80px 20px;
  background: var(--gradient-secondary);
  text-align: center;
}

.trusted-by h2 {
  font-size: 2.5rem;
  margin-bottom: 15px;
  background: var(--gradient-green-blue);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.partners-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  max-width: 1200px;
  margin: 40px auto 0;
  padding: 0 20px;
}

.partner-card {
  background: var(--glass-overlay);
  border-radius: 20px;
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  text-align: left;
}

.partner-card:hover {
  transform: translateY(-5px);
  background: var(--card-bg);
  border-color: rgba(0, 255, 179, 0.3);
}

.partner-image {
  width: 120px;
  height: 120px;
  margin: 0 auto 20px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid transparent;
  background: var(--gradient-green-blue);
  padding: 2px;
}

.partner-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.partner-info h3 {
  font-size: 1.5rem;
  margin: 0 0 5px;
  color: var(--text-primary);
  text-align: center;
}

.partner-title {
  color: var(--accent-green, #00ffb3);
  font-size: 1rem;
  margin: 0 0 20px;
  text-align: center;
}

.partner-description {
  color: var(--text-secondary);
  font-style: italic;
  line-height: 1.6;
  margin: 0 0 20px;
  font-size: 0.95rem;
}

.partner-achievements {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.partner-achievements span {
  color: var(--text-secondary);
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .trusted-by {
    padding: 60px 20px;
  }

  .trusted-by h2 {
    font-size: 2rem;
  }

  .partners-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .partner-card {
    padding: 20px;
  }
}

.instagram-warning {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 15px 20px;
  text-align: center;
  z-index: 9998; /* Sollte vor anderen Elementen liegen */
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.5);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.warning-content {
  max-width: 800px;
  margin: 0 auto;
}

.warning-icon {
  font-size: 2.5rem;
  margin-bottom: 10px;
}

.warning-content h3 {
  font-size: 1.5rem;
  margin-bottom: 10px;
  color: var(--accent-green, #00ffb3);
}

.warning-content p {
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 20px;
  color: rgba(255, 255, 255, 0.8);
}

.warning-button {
  background: var(--gradient-purple-pink);
  color: var(--text-primary);
  padding: 10px 20px;
  border-radius: 20px;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  margin: 0 10px;
  transition: background 0.3s;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.warning-button:hover {
  background: var(--gradient-pink-purple);
}

.warning-dismiss {
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-primary);
  padding: 10px 20px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  transition: background 0.3s;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.warning-dismiss:hover {
  background: rgba(255, 255, 255, 0.2);
}

@media (max-width: 768px) {
  .instagram-warning {
    padding: 10px 15px;
  }

  .warning-content h3 {
    font-size: 1.2rem;
  }

  .warning-content p {
    font-size: 0.9rem;
  }

  .warning-button,
  .warning-dismiss {
    padding: 8px 15px;
    font-size: 0.9rem;
  }
}
</style>